<template>
  <div>
    <Label class="text-sm font-medium">Color Adjustments</Label>
    <div class="mt-3 space-y-3">
      <!-- Hue -->
      <InheritedField
        :is-inherited="isInherited"
        :is-overridden="hueOverridden"
        :parent-scene-name="parentSceneName"
        @reset="$emit('resetField', 'effects.hue')"
      >
        <template #default="{ showInheritedBorder }">
          <div class="space-y-2">
            <div class="flex items-center justify-between">
              <Label 
                :class="['text-xs', showInheritedBorder ? 'text-orange-600' : 'text-muted-foreground']"
              >
                Hue
              </Label>
              <span class="text-xs text-muted-foreground">{{ hueValue }}°</span>
            </div>
            <Slider
              :model-value="[hueValue]"
              :max="180"
              :min="-180"
              :step="1"
              @update:model-value="updateHue"
            />
          </div>
        </template>
      </InheritedField>

      <!-- Tint -->
      <InheritedField
        :is-inherited="isInherited"
        :is-overridden="tintOverridden"
        :parent-scene-name="parentSceneName"
        @reset="$emit('resetField', 'effects.tint')"
      >
        <template #default="{ showInheritedBorder }">
          <div class="space-y-2">
            <div class="flex items-center justify-between">
              <Label 
                :class="['text-xs', showInheritedBorder ? 'text-orange-600' : 'text-muted-foreground']"
              >
                Tint
              </Label>
              <Input
                type="color"
                :model-value="tint"
                @input="handleTintUpdate"
                class="h-8 w-16 p-1 cursor-pointer"
                :class="{ 'border-orange-400': showInheritedBorder }"
                title="Color tint overlay"
              />
            </div>
          </div>
        </template>
      </InheritedField>

      <!-- Saturation -->
      <InheritedField
        :is-inherited="isInherited"
        :is-overridden="saturationOverridden"
        :parent-scene-name="parentSceneName"
        @reset="$emit('resetField', 'effects.saturation')"
      >
        <template #default="{ showInheritedBorder }">
          <div class="space-y-2">
            <div class="flex items-center justify-between">
              <Label 
                :class="['text-xs', showInheritedBorder ? 'text-orange-600' : 'text-muted-foreground']"
              >
                Saturation
              </Label>
              <span class="text-xs text-muted-foreground">{{ saturationValue }}%</span>
            </div>
            <Slider
              :model-value="[saturationValue]"
              :max="1000"
              :min="0"
              :step="1"
              @update:model-value="updateSaturation"
            />
          </div>
        </template>
      </InheritedField>

      <!-- Brightness -->
      <InheritedField
        :is-inherited="isInherited"
        :is-overridden="brightnessOverridden"
        :parent-scene-name="parentSceneName"
        @reset="$emit('resetField', 'effects.brightness')"
      >
        <template #default="{ showInheritedBorder }">
          <div class="space-y-2">
            <div class="flex items-center justify-between">
              <Label 
                :class="['text-xs', showInheritedBorder ? 'text-orange-600' : 'text-muted-foreground']"
              >
                Brightness
              </Label>
              <span class="text-xs text-muted-foreground">{{ brightnessValue }}%</span>
            </div>
            <Slider
              :model-value="[brightnessValue]"
              :max="2000"
              :min="0"
              :step="1"
              @update:model-value="updateBrightness"
            />
          </div>
        </template>
      </InheritedField>

      <!-- Contrast -->
      <InheritedField
        :is-inherited="isInherited"
        :is-overridden="contrastOverridden"
        :parent-scene-name="parentSceneName"
        @reset="$emit('resetField', 'effects.contrast')"
      >
        <template #default="{ showInheritedBorder }">
          <div class="space-y-2">
            <div class="flex items-center justify-between">
              <Label 
                :class="['text-xs', showInheritedBorder ? 'text-orange-600' : 'text-muted-foreground']"
              >
                Contrast
              </Label>
              <span class="text-xs text-muted-foreground">{{ contrastValue }}%</span>
            </div>
            <Slider
              :model-value="[contrastValue]"
              :max="300"
              :min="0"
              :step="1"
              @update:model-value="updateContrast"
            />
          </div>
        </template>
      </InheritedField>

      <!-- Blur -->
      <InheritedField
        :is-inherited="isInherited"
        :is-overridden="blurOverridden"
        :parent-scene-name="parentSceneName"
        @reset="$emit('resetField', 'effects.blur')"
      >
        <template #default="{ showInheritedBorder }">
          <div class="space-y-2">
            <div class="flex items-center justify-between">
              <Label 
                :class="['text-xs', showInheritedBorder ? 'text-orange-600' : 'text-muted-foreground']"
              >
                Blur
              </Label>
              <span class="text-xs text-muted-foreground">{{ blurValue }}px</span>
            </div>
            <Slider
              :model-value="[blurValue]"
              :max="50"
              :min="0"
              :step="1"
              @update:model-value="updateBlur"
            />
          </div>
        </template>
      </InheritedField>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import InheritedField from './InheritedField.vue'

interface Props {
  hue?: number
  tint?: string
  saturation?: number
  brightness?: number
  contrast?: number
  blur?: number
  isInherited?: boolean
  hueOverridden?: boolean
  tintOverridden?: boolean
  saturationOverridden?: boolean
  brightnessOverridden?: boolean
  contrastOverridden?: boolean
  blurOverridden?: boolean
  parentSceneName?: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  updateHue: [value: number]
  updateTint: [value: string]
  updateSaturation: [value: number]
  updateBrightness: [value: number]
  updateContrast: [value: number]
  updateBlur: [value: number]
  resetField: [fieldPath: string]
}>()

const hueValue = computed(() => props.hue ?? 0)
const saturationValue = computed(() => Math.round((props.saturation ?? 1) * 100))
const brightnessValue = computed(() => Math.round((props.brightness ?? 1) * 100))
const contrastValue = computed(() => Math.round((props.contrast ?? 1) * 100))
const blurValue = computed(() => props.blur ?? 0)

const updateHue = (value: number[] | undefined) => {
  if (value) emit('updateHue', value[0])
}

const handleTintUpdate = (event: Event) => {
  const target = event.target as HTMLInputElement
  emit('updateTint', target.value)
}

const updateSaturation = (value: number[] | undefined) => {
  if (value) emit('updateSaturation', value[0] / 100)
}

const updateBrightness = (value: number[] | undefined) => {
  if (value) emit('updateBrightness', value[0] / 100)
}

const updateContrast = (value: number[] | undefined) => {
  if (value) emit('updateContrast', value[0] / 100)
}

const updateBlur = (value: number[] | undefined) => {
  if (value) emit('updateBlur', value[0])
}
</script>
